import { useMutation } from '@tanstack/react-query';
import { trpc } from '@/utils/trpc';
import type {
  PresignedUrlRequest,
  PresignedUrlResponse,
  UploadCompletionResponse,
} from '@/types/file-upload';

const uploadFileToS3 = async (file: File, uploadUrl: string): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
};

export const useGetPresignedUrl = () => {
  const mutation = useMutation({
    ...trpc.requestsRouter.requestUploadUrl.mutationOptions(),
  });

  const getPresignedUrl = async (file: File): Promise<PresignedUrlResponse> => {
    const request: PresignedUrlRequest = {
      filename: file.name,
      metadata: {
        fileType: file.type,
        fileSize: file.size.toString(),
        originalName: file.name,
      },
    };

    return await mutation.mutateAsync(request);
  };

  return {
    getPresignedUrl,
    isLoading: mutation.isPending,
    error: mutation.error?.message,
    reset: mutation.reset,
    mutation,
  };
};

export const useFileUploadToPresignedUrl = () => {
  const mutation = useMutation({
    mutationFn: ({ file, uploadUrl }: { file: File; uploadUrl: string }) =>
      uploadFileToS3(file, uploadUrl),
  });

  const uploadToPresignedUrl = async (
    file: File,
    uploadUrl: string,
  ): Promise<void> => {
    await mutation.mutateAsync({ file, uploadUrl });
  };

  return {
    uploadToPresignedUrl,
    isLoading: mutation.isPending,
    error: mutation.error?.message,
    reset: mutation.reset,
    mutation,
  };
};

export const useMarkUploadCompleted = () => {
  const mutation = useMutation({
    ...trpc.requestsRouter.markUploadCompleted.mutationOptions(),
  });

  const markUploadCompleted = async (
    uploadId: string,
  ): Promise<UploadCompletionResponse> => {
    return await mutation.mutateAsync({ uploadId });
  };

  return {
    markUploadCompleted,
    isLoading: mutation.isPending,
    error: mutation.error?.message,
    reset: mutation.reset,
    mutation,
  };
};
